import { useState } from "react";
import { fmtAgo, fmtIn, fmtLastSeen, fmtStamp, initials, nItems } from "../lib/format";
import { PERM_LABEL, PERMS, TYPES } from "../lib/itemTypes";
import type { ClientPerm } from "../lib/itemTypes";
import type { CredRequest, Vault } from "../lib/types";
import { OWNER_ORG, useVault } from "../state/store";
import { Avatar, Badge, Button, Field, IconTile, Input, Select, Tabs, type Tone } from "../ui/ds";
import { useIsMobile } from "../ui/hooks";
import { Icon } from "../ui/Icon";
import { ItemsTab } from "./ItemsTab";
import { usePerspective, useUI, type Tab } from "./ui";
import { errMsg } from "../lib/api";

export function VaultView({ vault }: { vault: Vault }) {
  const { data, preview } = useVault();
  const p = usePerspective();
  const { nav, go, setModal } = useUI();
  const mobile = useIsMobile();
  const isClientVault = vault.kind === "client";
  const ownerActions = isClientVault && !p.asClient;
  const live = p.liveItems(vault);
  const members = data.members.filter((m) => m.vaultId === vault.id);
  const invites = data.invites.filter((i) => i.vaultId === vault.id);
  const people = members.length + invites.length;
  const perm = p.permOf(vault);

  const tabs: { value: Tab; label: string; count?: number | string }[] = p.asClient
    ? [
        { value: "items", label: "Items", count: p.canSee(vault) ? live.length : "" },
        { value: "requests", label: "Requests", count: p.waiting(vault) },
      ]
    : isClientVault
      ? [
          { value: "items", label: "Items", count: live.length },
          { value: "requests", label: "Requests", count: p.waiting(vault) },
          { value: "access", label: "Access", count: people },
        ]
      : [
          { value: "items", label: "Items", count: live.length },
          { value: "access", label: "Sharing" },
        ];
  const tab = tabs.some((t) => t.value === nav.tab) ? nav.tab : "items";

  const meta = p.asClient
    ? `${p.canSee(vault) ? `${nItems(live.length)} · ` : ""}your access: ${perm === "owner" ? "owner" : PERM_LABEL[perm].toLowerCase()}`
    : isClientVault
      ? `${nItems(live.length)} · ${live.filter((i) => !i.private).length} shared · ${people} ${people === 1 ? "person" : "people"} with access`
      : `${nItems(live.length)} · only you`;

  const showHeader = !(mobile && nav.pane === "detail" && tab === "items");
  const awaitingKey = !vault.key;

  return (
    <>
      {showHeader ? (
        <div className="page-head has-tabs">
          <div className="page-head-row">
            <div style={{ minWidth: 0 }}>
              {mobile ? (
                <button type="button" className="back-link" onClick={() => go({ view: "home" })}>
                  ← Vaults
                </button>
              ) : null}
              <h1 className="page-h1" style={{ marginTop: 0 }}>
                {vault.name}
              </h1>
              <div className="page-meta">{meta}</div>
            </div>
            <div className="page-actions">
              {ownerActions ? (
                <>
                  <Button variant="secondary" size="sm" icon="inbox" onClick={() => setModal({ kind: "request" })}>
                    Request credential
                  </Button>
                  <Button variant="secondary" size="sm" icon="user-plus" onClick={() => setModal({ kind: "share", tab: "invite", itemId: null })}>
                    Invite
                  </Button>
                </>
              ) : null}
              {mobile && p.canAdd(vault) ? (
                <Button size="sm" icon="plus" onClick={() => setModal({ kind: "item", itemId: null, vaultId: vault.id })}>
                  New
                </Button>
              ) : null}
            </div>
          </div>
          <div className="page-tabs">
            <Tabs items={tabs} value={tab} onChange={(t) => go({ tab: t, pane: "list" })} />
          </div>
        </div>
      ) : null}

      {awaitingKey ? (
        <div className="scroll page-body">
          <div className="card card-empty" style={{ maxWidth: 560 }}>
            <div className="card-title" style={{ marginBottom: 4 }}>
              Your access is nearly ready
            </div>
            {OWNER_ORG} shares the vault key with you the next time they open Agile Vault. You don’t need to do anything else
            {preview ? "." : " except keep your backup codes safe."}
          </div>
        </div>
      ) : tab === "items" ? (
        <ItemsTab vault={vault} />
      ) : tab === "requests" ? (
        <RequestsTab vault={vault} />
      ) : (
        <AccessTab vault={vault} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const REQ_TONE: Record<CredRequest["status"], Tone> = { waiting: "warning", received: "success", cancelled: "neutral", expired: "neutral" };
const REQ_LABEL: Record<CredRequest["status"], string> = { waiting: "Waiting", received: "Received", cancelled: "Cancelled", expired: "Expired" };

function RequestsTab({ vault }: { vault: Vault }) {
  const v = useVault();
  const p = usePerspective();
  const { go, setModal } = useUI();
  const reqs = p.requests(vault);
  const nowS = Date.now() / 1000;

  return (
    <div className="scroll page-body">
      <div className="stack-880" style={{ gap: 16 }}>
        {reqs.map((r) => {
          const showForm = p.asClient && r.status === "waiting";
          const meta = p.asClient
            ? r.status === "waiting"
              ? `From ${OWNER_ORG} · expires in ${fmtIn(r.expiresAt - nowS)}`
              : `You sent this ${fmtAgo(r.resolvedAt ?? r.createdAt)}`
            : r.status === "received"
              ? `From ${r.toName} · received ${fmtAgo(r.resolvedAt ?? r.createdAt)}`
              : r.status === "waiting"
                ? `To ${r.toName} · sent ${fmtAgo(r.createdAt)} · expires in ${fmtIn(r.expiresAt - nowS)}`
                : `To ${r.toName} · sent ${fmtAgo(r.createdAt)}`;
          const canOpen = !p.asClient && r.status === "received" && !!r.itemId && v.data.items.some((i) => i.id === r.itemId);
          return (
            <div className="card" key={r.id}>
              {showForm ? <div className="accent-rule" /> : null}
              <div className="req-card-row">
                <IconTile icon={TYPES[r.type]?.icon ?? "file"} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div className="req-title">{r.title}</div>
                  <div className="req-meta">{meta}</div>
                  <div className="req-fields">{r.fields.join(" · ")}</div>
                </div>
                <Badge tone={REQ_TONE[r.status]} dot>
                  {REQ_LABEL[r.status]}
                </Badge>
                {!p.asClient && r.status === "waiting" ? (
                  <Button variant="ghost" size="sm" onClick={() => void v.cancelRequest(r).catch((e) => v.toast("Couldn't cancel", errMsg(e), "danger"))}>
                    Cancel
                  </Button>
                ) : null}
                {canOpen ? (
                  <Button variant="secondary" size="sm" onClick={() => go({ tab: "items", cat: "all", itemId: r.itemId, pane: "detail", q: "" })}>
                    Open item
                  </Button>
                ) : null}
              </div>
              {r.msg ? <div className="mist-box" style={{ margin: "0 24px 16px" }}>“{r.msg}”</div> : null}
              {showForm ? <FulfilForm r={r} /> : null}
            </div>
          );
        })}
        {!reqs.length ? (
          <div className="card" style={{ padding: "32px 24px", display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <div className="card-title">No requests</div>
            <div className="card-sub" style={{ marginTop: 0 }}>
              {p.asClient ? `${OWNER_ORG} hasn’t asked you for anything.` : "Ask the client for a credential and it lands straight in this vault."}
            </div>
            {!p.asClient && vault.kind === "client" ? (
              <Button variant="secondary" size="sm" icon="inbox" onClick={() => setModal({ kind: "request" })}>
                Request credential
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FulfilForm({ r }: { r: CredRequest }) {
  const v = useVault();
  const [vals, setVals] = useState<string[]>(r.fields.map(() => ""));
  const [busy, setBusy] = useState(false);
  const tpl = TYPES[r.type]?.f ?? [];
  const submit = async () => {
    if (vals.some((x) => !x.trim())) {
      v.toast("Fill in every field", "Each one is needed to finish the request.", "warning");
      return;
    }
    setBusy(true);
    try {
      if (await v.fulfilRequest(r, vals)) v.toast(`Sent to ${OWNER_ORG}`, "It’s encrypted and in the vault now.");
    } catch (e) {
      v.toast("Couldn't send it", errMsg(e), "danger");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {r.fields.map((k, i) => {
        const t = tpl.find((x) => x[0] === k);
        return (
          <Field key={k} label={k}>
            <Input
              type={t?.[1] && !t?.[2] ? "password" : "text"}
              multiline={!!t?.[2]}
              autoComplete="off"
              value={vals[i]}
              onChange={(val) => setVals((s) => s.map((x, j) => (j === i ? val : x)))}
            />
          </Field>
        );
      })}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Button type="submit" variant="accent" arrow busy={busy}>
          Send securely
        </Button>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Encrypted on this device before it leaves.</span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Access (owner)
// ---------------------------------------------------------------------------

function AccessTab({ vault }: { vault: Vault }) {
  const v = useVault();
  const { setModal } = useUI();
  const { data } = v;
  const members = data.members.filter((m) => m.vaultId === vault.id);
  const invites = data.invites.filter((i) => i.vaultId === vault.id);
  const links = data.links.filter((l) => l.vaultId === vault.id);
  const acts = data.activity.filter((a) => a.vaultId === vault.id);
  const nowS = Date.now() / 1000;
  const fail = (what: string) => (e: unknown) => v.toast(what, errMsg(e), "danger");

  return (
    <div className="scroll page-body">
      <div className="stack-880">
        {vault.kind === "client" ? (
          <div className="card">
            <div className="accent-rule" />
            <div className="card-head" style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="card-title">People with access</div>
                <div className="card-sub">Everyone signs in with 2FA and saves backup codes before they can open anything.</div>
              </div>
              <Button variant="secondary" size="sm" icon="user-plus" onClick={() => setModal({ kind: "share", tab: "invite", itemId: null })}>
                Invite
              </Button>
            </div>
            {members.map((m) => (
              <div className="card-row" key={m.userId}>
                <Avatar initials={initials(m.name)} size={36} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="member-name">{m.name}</div>
                  <div className="member-email">{m.email}</div>
                  <div className="badges">
                    <Badge tone="success">2FA on</Badge>
                    {m.backupSaved ? <Badge tone="neutral">Backup codes saved</Badge> : <Badge tone="warning">Backup codes not saved</Badge>}
                    {!m.hasKey ? (
                      <Badge tone="neutral">{!m.backupSaved && v.session.settings.requireBackup ? "Waiting for backup codes" : "Access being set up"}</Badge>
                    ) : null}
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 96 }}>
                  {fmtLastSeen(m.lastSeen)}
                </div>
                <div style={{ width: 150 }}>
                  <Select<ClientPerm>
                    size="sm"
                    label={`Permission for ${m.name}`}
                    options={PERMS.map((x) => ({ value: x, label: PERM_LABEL[x] }))}
                    value={m.perm}
                    onChange={(x) => void v.setMemberPerm(m, x).catch(fail("Couldn't change that"))}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => void v.revokeMember(m).catch(fail("Couldn't revoke access"))}>
                  Revoke
                </Button>
              </div>
            ))}
            {invites.map((i) => (
              <div className="card-row" key={i.id}>
                <Avatar initials={initials(i.name || i.email)} size={36} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="member-name">{i.name || i.email.split("@")[0]}</div>
                  <div className="member-email">{i.email}</div>
                  <div className="badges">
                    <Badge tone="neutral">Invite pending</Badge>
                    {i.expiresAt < nowS ? <Badge tone="warning">Invite expired</Badge> : null}
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 96 }}>
                  Not signed in
                </div>
                <div style={{ width: 150, fontSize: 13, color: "var(--text-muted)" }}>{PERM_LABEL[i.perm]}</div>
                <Button variant="ghost" size="sm" onClick={() => void v.revokeInvite(i).catch(fail("Couldn't revoke the invite"))}>
                  Revoke
                </Button>
              </div>
            ))}
            {!members.length && !invites.length ? (
              <div className="card-empty">Nobody else has access yet. Invite the client to share this vault with them.</div>
            ) : null}
          </div>
        ) : null}

        <div className="card">
          <div className="card-head">
            <div className="card-title">One-time links</div>
            <div className="card-sub">Links expire on their own. Revoke one to stop it working now.</div>
          </div>
          {links.map((l) => {
            const title = data.items.find((i) => i.id === l.itemId)?.title ?? "Deleted item";
            const state = l.status === "revoked" ? "Revoked" : l.views >= l.maxViews ? "Used" : l.expiresAt < nowS ? "Expired" : "Active";
            const when =
              state === "Active" ? `Expires in ${fmtIn(l.expiresAt - nowS)}` : state === "Revoked" ? "Revoked" : state === "Used" ? `Used up` : `Expired ${fmtStamp(l.expiresAt)}`;
            return (
              <div className="card-row" key={l.id} style={{ padding: "14px 24px" }}>
                <Icon name="link" size={16} style={{ color: "var(--deep-teal)" }} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="member-name">{title}</div>
                  <div className="mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {when} · {l.views} of {l.maxViews} {l.maxViews === 1 ? "view" : "views"}
                    {l.passphrase ? " · passphrase" : ""}
                  </div>
                </div>
                <Badge tone={state === "Active" ? "success" : "neutral"}>{state}</Badge>
                {state === "Active" ? (
                  <Button variant="ghost" size="sm" onClick={() => void v.revokeLink(l).catch(fail("Couldn't revoke the link"))}>
                    Revoke
                  </Button>
                ) : null}
              </div>
            );
          })}
          {!links.length ? <div className="card-empty">No links yet. Open an item and choose Share.</div> : null}
        </div>

        <div className="card">
          <div className="card-head card-title">Activity</div>
          {acts.map((a) => (
            <div className="act-row" key={a.id}>
              <div className="act-time">{fmtStamp(a.createdAt)}</div>
              <div className="act-text">
                <strong>{a.who}</strong> {a.what}
              </div>
            </div>
          ))}
          {!acts.length ? <div className="card-empty">Nothing yet. Every view, copy, edit, share and revoke shows here.</div> : null}
        </div>
      </div>
    </div>
  );
}
